import React from 'react';
import { Link as RRLink } from 'react-router-dom';
import { Box, Button, Flex, Icon, IconButton, Link, Text, Heading, useSnackbar } from 'pounceV2';
import { extractErrorMessage, getElapsedTime } from 'Helpers/utils';
import { Notification as NotificationType } from 'Generated/schema';
import dayjs from 'dayjs';
import useHover from 'Hooks/useHover';
import urls from 'Source/urls';
import { LinkButtonV3 } from 'Components/buttons/LinkButton/LinkButtonV3';
import { useCSVDownloadV3 } from 'Hooks/useCSVDownloadV3';
import { useTheme } from '@mui/material';
import type { IconType } from 'pounceV2/src/theme/icons';
import { useUpdateNotification } from './graphql/UpdateNotification.generated';
import { notificationsIcons } from './constants';

interface CSVNotificationProps {
  notification: NotificationType;
}

const CSVNotificationV3: React.FC<CSVNotificationProps> = ({ notification }) => {
  const { isHovering, handlers: hoverHandlers } = useHover();
  const { pushSnackbar } = useSnackbar();
  const now = dayjs().utc().toISOString();
  const archived = Date.parse(notification.archivedAt) > 0;
  const read = Date.parse(notification.readAt) > 0;
  const theme = useTheme();
  const { originalQueryId: queryID, presignedUrl, presignedUrlTtl } = notification.metadata;

  const isUrlExpired = React.useMemo(() => {
    if (!presignedUrlTtl) {
      return false;
    }
    const expirationDate = new Date(presignedUrlTtl);
    return new Date() > expirationDate;
  }, [presignedUrlTtl]);

  const [updateNotification] = useUpdateNotification({
    onError: error => {
      pushSnackbar({
        variant: 'error',
        title: extractErrorMessage(error),
      });
    },
  });

  const { downloadCSVResults } = useCSVDownloadV3({
    queryId: queryID,
    isAsyncDownload: true,
  });

  const regenerateCSV = () => {
    downloadCSVResults();
  };

  const icon = notificationsIcons[notification.level] ?? {
    type: 'notifications-info',
    color: theme.palette.tokens.content.assets.semantic.info.base,
  };

  const isSuccess = notification.level === 'INFO';

  return (
    <Flex
      align="flex-start"
      justify="space-between"
      gap={5}
      px={6}
      py={4}
      borderBottom="1px solid"
      borderBottomColor={theme.palette.tokens.stroke.neutral[5]}
      backgroundColor={
        read && !archived
          ? theme.palette.tokens.background.neutral.inverse.raised[4]
          : theme.palette.tokens.background.neutral.mid[3]
      }
      {...hoverHandlers}
    >
      <Icon type={icon.type as IconType} color={icon.color as string} />

      <Flex align="flex-start" direction="column" gap={0} flex={1}>
        <Flex align="flex-start" justify="space-between" width="100%">
          <Box flex={1} mr={3}>
            <Text
              variant="label/lg"
              fontWeight="semibold"
              color={
                read || archived
                  ? theme.palette.tokens.content.text.neutral[3]
                  : theme.palette.tokens.content.text.neutral.base
              }
            >
              {isSuccess ? 'CSV Download Ready' : 'CSV Failed to Generate'}
            </Text>

            <Text
              variant="label/sm"
              color={theme.palette.tokens.content.text.accent[14][3]}
              fontWeight="semibold"
              mt={1}
            >
              {getElapsedTime(new Date(notification.createdAt).getTime() / 1000)}
            </Text>
          </Box>

          {isHovering && !archived && (
            <Flex align="center" gap={2}>
              <IconButton
                variant="outline"
                icon="archive"
                size="small"
                title="Archive Notification"
                aria-label="Archive Notification"
                onClick={() => {
                  updateNotification({
                    variables: {
                      input: { id: notification.id, archivedAt: now },
                    },
                  });
                }}
              />
              {read ? (
                <IconButton
                  variant="outline"
                  icon="mark-as-unread"
                  size="small"
                  title="Mark Notification As Unread"
                  aria-label="Mark Notification As Unread"
                  onClick={() => {
                    updateNotification({
                      variables: {
                        input: { id: notification.id, readAt: '0001-01-01T00:00:00Z' },
                      },
                    });
                  }}
                />
              ) : (
                <IconButton
                  variant="outline"
                  icon="mark-as-read"
                  size="small"
                  title="Mark Notification As Read"
                  aria-label="Mark Notification As Read"
                  onClick={() => {
                    updateNotification({
                      variables: {
                        input: { id: notification.id, readAt: now },
                      },
                    });
                  }}
                />
              )}
            </Flex>
          )}
        </Flex>

        <Heading
          variant="h6"
          wordBreak="break-word"
          mb={4}
          color={
            read || archived
              ? theme.palette.tokens.content.text.neutral[3]
              : theme.palette.tokens.content.text.neutral.base
          }
        >
          {isSuccess ? (
            <>
              CSV from search query{' '}
              <Link size="large" as={RRLink} to={urls.investigate.search({ queryId: queryID })}>
                {queryID}
              </Link>{' '}
              is ready for download. Link is only valid for 12 hours from generation
            </>
          ) : (
            <>
              There was a problem with generating the CSV from search query{' '}
              <Link size="large" as={RRLink} to={urls.investigate.search({ queryId: queryID })}>
                {queryID}
              </Link>
            </>
          )}
        </Heading>

        {!archived ? (
          <Box>
            {isSuccess ? (
              <LinkButtonV3
                external
                disabled={isUrlExpired}
                to={presignedUrl}
                size="medium"
                variant="primary"
              >
                Download CSV
              </LinkButtonV3>
            ) : (
              <Button variant="primary" onClick={regenerateCSV} size="medium">
                Retry Generating CSV
              </Button>
            )}
          </Box>
        ) : (
          <Button
            variant="outline"
            size="small"
            onClick={() => {
              updateNotification({
                variables: {
                  input: { id: notification.id, archivedAt: '0001-01-01T00:00:00Z' },
                },
              });
            }}
          >
            Unarchive
          </Button>
        )}
      </Flex>
    </Flex>
  );
};

export default CSVNotificationV3;
